import { useEffect, useState } from "react";
import api from "../../../services/api";
import type { Tenant } from "../../../types/tenant";
import { Trash2, Edit, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Table } from "../../../components/table";
import { BackButton } from "../../../components/button/back-button";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import { formatPhoneWithCountryCode, getApiErrorMessage } from "../../../utils";

type FindAllTenantDto = {
  data: Tenant[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export default function ListTenants() {
  const { success, error } = useToast();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const columns = [
    { key: "appName", label: "App Name" },
    {
      key: "phone",
      label: "WhatsApp",
      render: (tenant: Tenant) =>
        formatPhoneWithCountryCode(tenant.phone) || "-",
    },
    { key: "email", label: "E-mail" },
    {
      key: "actions",
      label: "Ações",
      render: (tenant: Tenant) => (
        <div className="text-center flex justify-center gap-3">
          <button
            onClick={() =>
              navigate(`/painel/config/tenant/editar/${tenant.appName}`)
            }
            className="btn-link-primary cursor-pointer"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            //onClick={() => setTenantToDelete(tenant)}
            className="btn-link-danger cursor-pointer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    getTenants(1);
  }, []);

  async function getTenants(pageNumber = 1) {
    try {
      const response = await api.get<FindAllTenantDto>("/tenant", {
        params: { page: pageNumber, limit },
      });

      setTenants(response.data.data);
      setPage(response.data.page);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  async function deleteTenant(appName: string) {
    if (isDeleting) return;
    try {
      setIsDeleting(true);
      await api.delete(`/tenant/${appName}`);
      setTenants((prev) => prev.filter((t) => t.appName !== appName));
      setTenantToDelete(null);
      success("Tenant deletado com sucesso!");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="app-page">
      <BackButton />

      <div className="flex items-center justify-between mb-6">
        <h1 className="app-page-title">Tenants</h1>
        <button
          onClick={() => navigate("/painel/config/tenant/cadastro")}
          className="btn btn-neutral cursor-pointer"
        >
          <Plus className="w-4 h-4" /> Novo Tenant
        </button>
      </div>

      <Table<Tenant>
        columns={columns}
        data={tenants}
        page={page}
        total={total}
        totalPages={totalPages}
        onPageChange={(newPage) => getTenants(newPage)}
      />

      <ConfirmationModal
        isOpen={!!tenantToDelete}
        title="Confirmar exclusão"
        description={
          tenantToDelete
            ? `Tem certeza que deseja excluir o tenant ${tenantToDelete.appName}?`
            : ""
        }
        onCancel={() => setTenantToDelete(null)}
        onConfirm={() => tenantToDelete && deleteTenant(tenantToDelete.appName)}
        isLoading={isDeleting}
      />
    </div>
  );
}
